import { Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { RootState } from '@app/store/root-state';
import { BehaviorSubject, combineLatest, combineLatestWith, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { RaceFacadeService } from '@app/facade-services/race/race-facade.service';
import { RaceClassFacadeService } from '@app/facade-services/race-class/race-class-facade.service';
import { FenceFacadeService } from '@app/facade-services/fence/fence-facade.service';
import { selectRaceByRaceGuid } from '@app/store/faxe/race/race.selectos';
import { selectRaceClassByRaceGuid } from '@app/store/faxe/race-class/race-class.selectos';
import { defined, isNotEmpty, makeBooleanHashKey } from '@app/fremework/functions';
import { selectFenceByRaceGuid } from '@app/store/faxe/fences/fence.selectos';
import { FenceRace } from '@app/store/faxe/fences/model/fence-race';
import { RaceView } from '@app/facade-services/race-view/model/race-view';
import { SelectedRaceService } from '@app/services/selected-race.service';
import { RiderViewService } from '@app/facade-services/rider-view/rider-view.service';
import { RaceClass } from '@app/rest-client/faxe/models/race-class';
import { Race } from '@app/rest-client/faxe/models/race';

@Injectable({
  providedIn: 'root'
})
export class RaceViewService implements OnDestroy {

  constructor(private store: Store<RootState>,
              private readonly raceFacadeService: RaceFacadeService,
              private readonly raceClassFacadeService: RaceClassFacadeService,
              private readonly fenceFacadeService: FenceFacadeService,
              private readonly selectedRaceService: SelectedRaceService,
              private readonly riderViewService: RiderViewService) {
  }

  private mapLock = new BehaviorSubject<boolean>(false);
  private unsubscribe$ = new Subject<void>();
  private raceViewSubject = new BehaviorSubject<RaceView>(null);
  public raceView$: Observable<RaceView> = this.raceViewSubject.asObservable();

  public selectRaceView(allRequired: boolean, fetch: boolean, allGeneretions: boolean, issuer: string): Observable<RaceView> {
    return this.mapLock.pipe(
      filter((locked) => !locked), // Wait until the lock is released
      take(1), // Ensure only one thread proceeds
      switchMap(() => {
        this.mapLock.next(true); // Acquire the lock
        try {
          this.selectRaceViewInternal(allRequired, fetch, allGeneretions, issuer)
            .pipe(takeUntil(this.unsubscribe$),
              map((raceView) => {
                this.raceViewSubject.next(raceView);
              })
            ).subscribe();
          return this.raceView$;
        } finally {
          this.mapLock.next(false); // Release the lock
        }
      })
    );
  }

  private selectRaceViewInternal(allRequired: boolean, fetch: boolean, allGenerations: boolean, issuer: string): Observable<RaceView> {
    return this.selectedRaceService.selectedRace$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(race => defined(race)),
        switchMap(race =>
          combineLatest([
            this.selectRaceViewRace(race.raceGuid, fetch, issuer),
            this.riderViewService.selectRiderViews(this.selectedRaceService.selectedRaceGuid, fetch, allRequired, allGenerations, issuer),
            race.raceGuid
          ])
        ),
        map(([raceView, riderViews, _]) => {
          let rv = {
            ...raceView,
            riders: riderViews
          };
          rv.hashCode = this.hashCode(rv);
          return rv;
        })
      );
  }

  public selectedRaceGuid(): string {
    return this.selectedRaceService.selectedRaceGuid;
  }

  private hashCode(rv: RaceView): string {
    return `R ${defined(rv?.raceGuid)}
    RC ${rv.raceClassView?.length}
    F ${rv.raceClassView?.flatMap(rcv => rcv.fences)?.length}
    R (${rv.riders?.filter(r => r.isReportable).length}, ${rv.riders?.filter(r => !r.isReportable).length})
    FR ${rv.riders?.flatMap(r => r.fenceResults)?.length}
    RE ${rv.riders?.flatMap(r => r.riderEvents)?.length}`;
  }

  private selectRaceViewRace(raceGuid: string, fetch: boolean, issuer: string): Observable<RaceView> {
    if (fetch) {
      this.raceFacadeService.fetchRaceByRaceGuid(raceGuid, issuer);
      this.raceClassFacadeService.fetchRaceClass(raceGuid, issuer);
      this.fenceFacadeService.fetchFence(raceGuid, issuer);
    }

    return this.selectRace(raceGuid)
      .pipe(filter(race => defined(race)),
        combineLatestWith(this.selectRaceClassByRaceGuid(raceGuid)
            .pipe(filter(raceClasses => isNotEmpty(raceClasses))),
          this.selectFenceRaces(raceGuid)
            .pipe(filter(fences => isNotEmpty(fences)))),
        map(([race, raceClasses, fences]) =>
          this.mapAndFilter(race, raceClasses, fences))
      );
  }

  private selectRace(raceGuid: string): Observable<Race> {
    return this.store.pipe(
      select(selectRaceByRaceGuid(raceGuid)));
  }

  private selectRaceClassByRaceGuid(raceGuid: string): Observable<RaceClass[]> {
    return this.store.pipe(
      select(selectRaceClassByRaceGuid(raceGuid)),
      filter((data) => isNotEmpty(data)));
  }

  private selectFenceRaces(raceGuid: string): Observable<FenceRace[]> {
    return this.store.pipe(
      select(selectFenceByRaceGuid(raceGuid)));
  }

  private mapAndFilter(race: Race, raceClasses: RaceClass[], fenceRaces: FenceRace[]): RaceView {
    console.log('R ' + defined(race), 'RC ' + raceClasses?.length, 'F ' + fenceRaces?.length);
    return ({
      ...race,
      raceClassView: raceClasses.map(raceClass => ({
        ...raceClass,
        fences: fenceRaces.filter(fence => fence.raceClassGuid === raceClass.raceClassGuid)
      }))
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
